/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useLocation } from 'js/lib/useRouter';

import { breakpoints } from '@coursera/cds-core';

import type { ConsumerPageHeader_EnterpriseProgramFragment as EnterpriseProgramFragment } from 'bundles/composable-page-header-consumer/queries/__generated__/ConsumerMyPageHeaderQuery';
import {
  ORG_SCOPED_ROUTE_PREFIX,
  isOnOrgScopedRoute,
} from 'bundles/composable-page-header-enterprise/utils/orgScopedRouteUtils';
import EnterpriseSearchBar from 'bundles/search-common/components/searchbar/enterprise/SearchBar';
import { useResolvedProgramIds } from 'bundles/unified-common/contexts/org-scoped/OrgScopedImpersonationContext';

import _t from 'i18n!nls/composable-page-header-enterprise';

type Props = {
  thirdPartyOrganizationSlug?: string;
  enrolledEnterprisePrograms?: Partial<EnterpriseProgramFragment>[] | null;
  chosenProgramSlug?: string;
  chosenProgramId?: string;
};

const styles = {
  searchBar: css`
    width: 100%;
    max-width: 480px;
    min-width: 0;

    ${breakpoints.up('lg')} {
      width: 100%;
    }
  `,
};

export function EnterpriseOrgScopedSearchBarGridItem(props: Props) {
  const { thirdPartyOrganizationSlug, enrolledEnterprisePrograms, chosenProgramSlug, chosenProgramId } = props;
  const { pathname } = useLocation();
  const { programIds: resolvedProgramIds } = useResolvedProgramIds();
  const onOrgScopedRoute = isOnOrgScopedRoute(pathname);

  if (!thirdPartyOrganizationSlug) {
    return null;
  }

  const orgPrograms = enrolledEnterprisePrograms?.filter(
    (program) => program?.naptimeThirdPartyOrganization?.slug === thirdPartyOrganizationSlug
  );

  const enrolledProgramIds = orgPrograms?.flatMap((program) => (program?.id ? [program.id] : [])) ?? [];
  // Fall back to the current page's program so logged-in users without enrollments
  // in this org (e.g. admins and CSMs viewing a program) still get a scoped search bar.
  const fallbackProgramIds: Array<string> =
    enrolledProgramIds.length === 0 && chosenProgramId ? [chosenProgramId] : enrolledProgramIds;
  // On org pages the resolver returns the previewed programs during an admin preview.
  const programIds = onOrgScopedRoute && resolvedProgramIds.length ? resolvedProgramIds : fallbackProgramIds;
  const primaryProgramSlug = chosenProgramSlug ?? orgPrograms?.[0]?.slug;

  // For non-org-scoped mode, a program slug and scope are needed for the browse path.
  if (!onOrgScopedRoute && (!primaryProgramSlug || !programIds.length)) {
    return null;
  }

  const orgName = orgPrograms?.[0]?.naptimeThirdPartyOrganization?.name;
  const searchPath = onOrgScopedRoute
    ? `${ORG_SCOPED_ROUTE_PREFIX}${thirdPartyOrganizationSlug}/search`
    : `/programs/${primaryProgramSlug}/browse`;

  return (
    <div data-testid="org-scoped-enterprise-search-bar" css={styles.searchBar}>
      <EnterpriseSearchBar
        enterpriseSearchBarConfig={{
          programIds,
          pathname: searchPath,
          searchResultsBasePath: searchPath,
          placeholder: orgName
            ? _t('Search #{orgName} learning on Coursera', { orgName })
            : _t('Search learning on Coursera'),
          ...(onOrgScopedRoute
            ? { enterpriseOrgSlug: thirdPartyOrganizationSlug }
            : { enterpriseProgramSlug: primaryProgramSlug }),
        }}
      />
    </div>
  );
}

export default EnterpriseOrgScopedSearchBarGridItem;
